import { AxiosResponse } from 'axios';
import Observable from '../Observable';
import { InteractionStatus } from '../../../../../types/InteractionStatus';

import processParameters from './utils/processParameters';
import mapFieldsetsWithParams from './utils/mapFieldsetsWithParams';
import hasChangeableParameters from './utils/hasChangeableParameters';
import updateModel from './utils/updateModel';
import sendRequest from './utils/sendRequest';
import forEachParameter from './utils/forEachParameter';
import formDataExtractor from './utils/formDataExtractor';
import IParameter from '../../../../../types/IParameter';
import { SonApiResponse } from '../../../../../types/api/SonApiResponse';

export interface IFormModel {
  /** url attribute for the <form> element */
  url: string;
  /** method attribute for the <form> element */
  method: 'POST' | 'GET';
  /** navigation object from react-router */
  navigate: (to: string, options?: any) => void;
  /** params aka inputs from the JSON */
  params?: IParameter[];
  name?: string;
  /** boolean causing a check before creating new resources */
  checkPageExists?: boolean;
  /** boolean allowing a form to be submittable even if `hasChanges` returns false */
  submittableWithoutChange?: boolean;
  validateResponseStatus?: (status: number) => boolean;
}

export default class FormModel extends Observable {
  public name?: string;
  public method: any;
  public parameter: Record<string, IParameter>;
  public readonly validateResponseStatus?: (status: number) => boolean;

  private readonly _fieldSets: Array<{ fieldSet: string; inputs: IParameter[] }>;
  private readonly _submittableWithoutChange?: boolean;
  private readonly _checkPageExists?: boolean;
  private readonly _navigate: (to: string, options?: any) => void;
  private _status: string = InteractionStatus.NONE;
  private _response: AxiosResponse<SonApiResponse> | undefined = undefined;
  private _url: string;

  constructor({
    url,
    method,
    navigate,
    params,
    name,
    checkPageExists,
    submittableWithoutChange,
    validateResponseStatus,
  }: IFormModel) {
    super();

    this.name = name;
    this._url = url;
    this.method = method;
    this.parameter = processParameters(params, this.broadcast.bind(this));
    this._submittableWithoutChange = submittableWithoutChange;

    this._navigate = navigate;
    this._fieldSets = mapFieldsetsWithParams(this.parameter);
    this._checkPageExists = checkPageExists;
    this.validateResponseStatus = validateResponseStatus;
  }

  set status(value) {
    this._status = value;
    this.broadcast();
  }

  get status() {
    return this._status;
  }

  set url(newUrl) {
    this._url = newUrl;
    this.broadcast();
  }

  get url() {
    return this._url;
  }

  get response() {
    return this._response;
  }

  get fieldSets() {
    return this._fieldSets;
  }

  /**
   * Retrieve URL from response. Backend typically sends them in data.url of response.
   * @returns string or null
   */
  public getResponseUrl = () => {
    if (this._response?.data?.errorType && this._response?.data?.errorPage?.url) {
      return this._response?.data?.errorPage?.url;
    }

    if (this._response?.data?.url) {
      return this._response?.data?.url;
    }

    return null;
  };

  public isValid = () => {
    let isValid = true;

    forEachParameter(this.parameter).do((parameter: IParameter) => {
      if (parameter) {
        if (parameter.isValid && !parameter.isValid()) {
          isValid = false;
        }
      }
    });

    return isValid;
  };

  public hasChanges = () => {
    let hasChanges = false;

    forEachParameter(this.parameter).do((parameter: IParameter) => {
      if (parameter) {
        if (parameter.valueChanged && parameter.valueChanged()) {
          hasChanges = true;
        }
      }
    });

    return hasChanges;
  };

  private getParams = () => {
    if (this.parameter.imageuploadparams) {
      return this.parameter.imageuploadparams.params;
    }
    return this.parameter;
  };

  public reset = () => {
    forEachParameter(this.getParams()).do((parameter: IParameter) => {
      if (parameter?.reset) {
        parameter.reset();
      }
    });

    this.broadcast();
  };

  /**
   * method to empty form model values we don't clear
   * imageType value because backend needs for identifying service.
   * */
  public clear = (hard = false) => {
    forEachParameter(this.getParams()).do((parameter: IParameter) => {
      if (parameter) {
        if (parameter.clear && parameter.name !== 'imageType') {
          parameter.clear(hard);
        }
      }
    });

    this.broadcast();
  };

  public changeValue = (parameterName: string, value: any) => {
    if (this.parameter[parameterName] && this.isNewValue(this.parameter[parameterName].value, value)) {
      if (this.status === InteractionStatus.SUCCESS) {
        this._status = InteractionStatus.NONE;
      }
      this.parameter[parameterName].value = value;
    }
  };

  public appendParameter = (name: string, value: any) => {
    // @ts-expect-error needs to be put into the garbage
    this.parameter[name] = {
      clear(): void {},
      getIssues(): any[] {
        return [];
      },
      reset(): void {},
      type: '',
      updateOriginalValue(): void {},
      valueChanged(): boolean {
        return false;
      },
      name,
      value,
      appendedParameter: true,
      isValid: () => true,
      getPayloadValue: () => ({ name, value }),
    };
  };

  public submitActionEnabled = () => {
    if (this._submittableWithoutChange) {
      return true;
    }

    // If Form has no parameters the submitAction will be enabled also when there are no changes
    return (
      this.status !== InteractionStatus.SENDING &&
      this.status !== InteractionStatus.SUCCESS &&
      (hasChangeableParameters(this.parameter) ? this.hasChanges() : true)
    );
  };

  public submit = async (customExtractor?: (parameter: Record<string, any>) => FormData | URLSearchParams) => {
    if (this.submitActionEnabled()) {
      this._response = undefined;
      if (this.method === 'POST') {
        this.status = InteractionStatus.SENDING;
        try {
          let payload;
          if (customExtractor) {
            payload = customExtractor(this.parameter);
          } else {
            payload = formDataExtractor(this.parameter);
          }

          this._response = await sendRequest({
            payload,
            validateResponseStatus: this.validateResponseStatus,
            checkPageExists: this._checkPageExists,
            url: this.url,
          });
          this.status = InteractionStatus.SUCCESS;
          updateModel(this.parameter);
          // explicitly return response for contentBlockForm
          // so we can hook into modal and await response -> extra Wurst but ok
          return this._response;
        } catch (error) {
          this._response = (error as any).response;
          this.status = InteractionStatus.FAIL;
          throw error;
        }
      } else if (this.method === 'GET') {
        const payload = formDataExtractor(this.parameter, this.method);
        let navigateUrl = this.url;
        if (payload) {
          navigateUrl += `?${payload?.toString()}`;
        }
        this._navigate(navigateUrl, {
          state: {
            refetch: 'force',
          },
        });
      }
    } else {
      throw new Error('Submit not enabled!');
    }
  };

  private isNewValue(currentValue: any, value: any) {
    if (Array.isArray(currentValue) && Array.isArray(value)) {
      if (currentValue.length !== value.length) {
        return true;
      }

      if (JSON.stringify(currentValue) !== JSON.stringify(value)) {
        return true;
      }

      return currentValue.some(element => !value.includes(element));
    }

    return currentValue !== value;
  }
}
